import { Icon } from '@iconify/react';
import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';

import Checkbox from '@/components/Form/Fields/Checkbox';
import Text from '@/components/Text';
import type { ExternalCandidateList } from '@/services/externalCandidates';
import type { OfferSearch } from '@/services/reasoningSearch';
import type { Filters } from '@/stores/useDashboardFiltersStore';
import { dateFormatter } from '@/utils/date';

import SeniorityChip from '../SeniorityChip';
import CezResume from './Cells/CezResume';
import Collections from './Cells/Collections';
import FullName from './Cells/FullName';
import Languages from './Cells/Languages';
import MessagesDialog from './Cells/MessagesDialog';
import NotesPopover from './Cells/NotesPopover';
import Preview from './Cells/Preview';
import Resume from './Cells/Resume';
import Salary from './Cells/Salary';
import Settings from './Cells/Settings';
import Skills from './Cells/Skills';
import EditableCell from './EditableCell';
import { salaryHeader } from './Headers/salaryHeader';
import SettingsHeader from './Headers/SettingsHeader';
import ProcessingWrapper from './ProcessingWrapper';

const columnHelper = createColumnHelper<
  ExternalCandidateList & {
    reasoningSearchResult?: OfferSearch;
    filters?: Filters;
  }
>();

type ColumnsParams = {
  onRetry: (id: number) => void;
  profileEmailDomain?: string;
};

export const columns = ({ onRetry, profileEmailDomain }: ColumnsParams) => [
  columnHelper.display({
    id: 'select',
    cell: ({ row }) => {
      return (
        <Checkbox
          {...{
            variant: 'transparent',
            className: '!p-0',
            name: `check${row.index.toString()}`,
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      );
    },
  }),
  columnHelper.display({
    id: 'preview',
    header: 'Preview',
    cell: ({ row, table }) => (
      <Preview
        candidateId={row.original.id}
        onMouseEnter={() =>
          table.options.meta?.onProfilePrefetch(row.original.id)
        }
      />
    ),
  }),
  columnHelper.accessor('name', {
    header: 'Full name',
    cell: (cellProps) => <FullName cellProps={cellProps} onRetry={onRetry} />,
  }),
  columnHelper.accessor('candidateDetailedInfo.jobTitle', {
    header: 'Job title',
    size: 300,
    cell: ({ getValue }) => {
      return <Text variant="body-small">{getValue()}</Text>;
    },
  }),
  columnHelper.display({
    id: 'resume',
    header: 'Resume',
    cell: ({ row, table }) => (
      <div className="flex flex-row">
        <Resume row={row} table={table} />
      </div>
    ),
  }),
  ...(['admin.com', 'inetum.com'].includes(profileEmailDomain ?? '')
    ? [
        columnHelper.display({
          id: 'cez',
          header: 'CEZ Resume',
          cell: ({ row, table }) => (
            <div className="flex flex-row">
              <CezResume row={row} table={table} />
            </div>
          ),
        }),
      ]
    : []),
  columnHelper.accessor('candidateDetailedInfo.seniorityLevel', {
    header: 'Seniority',
    cell: ({ getValue, row }) => {
      return (
        <ProcessingWrapper row={row}>
          <SeniorityChip seniority={getValue()} />
        </ProcessingWrapper>
      );
    },
  }),
  ...(['admin.com', 'mindpal.co'].includes(profileEmailDomain ?? '')
    ? [
        columnHelper.accessor('candidateDetailedInfo.videoFile', {
          header: 'Video',
          cell: ({ getValue }) => {
            return (
              <Icon
                icon="material-symbols:hangout-video-rounded"
                fontSize={24}
                className={classNames('ml-1', {
                  'text-success': !!getValue(),
                  'text-error': !getValue(),
                })}
              />
            );
          },
        }),
        columnHelper.accessor('candidateDetailedInfo.offer', {
          header: 'Applied on offer',
          size: 300,
          cell: ({ getValue }) => {
            return <Text variant="body-small">{getValue()?.title}</Text>;
          },
        }),
      ]
    : []),
  columnHelper.accessor('candidateDetailedInfo.skills', {
    header: 'Skills',
    cell: ({ getValue, row }) => {
      const { aiParsedFilters, keywords } =
        row.original.reasoningSearchResult ?? {};
      const { skills } = row.original.filters ?? {};

      return (
        <ProcessingWrapper row={row}>
          <Skills
            value={getValue()}
            filterSkills={[
              ...(aiParsedFilters?.skills ?? []),
              ...(keywords?.keywords ?? []),
              ...(keywords?.suggestedKeywords ?? []),
              ...(skills ?? []),
            ]}
          />
        </ProcessingWrapper>
      );
    },
  }),
  columnHelper.accessor('languages', {
    header: 'Languages',
    cell: ({ getValue, row }) => (
      <ProcessingWrapper row={row}>
        <Languages value={getValue()} />
      </ProcessingWrapper>
    ),
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (cellProps) => {
      if (cellProps.table.options.meta?.editModeActive)
        return <EditableCell cellProps={cellProps} />;
      return <Text variant="body-small">{cellProps.getValue()}</Text>;
    },
  }),
  columnHelper.accessor('phoneNumber', {
    header: 'Phone number',
    cell: (cellProps) => {
      if (cellProps.table.options.meta?.editModeActive)
        return <EditableCell type="number" cellProps={cellProps} />;
      return (
        <Text variant="body-small" className="truncate">
          {cellProps.getValue()}
        </Text>
      );
    },
  }),
  columnHelper.accessor('collections', {
    header: 'Collections',
    cell: ({ row, getValue }) => <Collections value={getValue()} row={row} />,
  }),

  columnHelper.accessor('createdAt', {
    header: 'Created at',
    cell: ({ getValue }) =>
      dateFormatter({
        date: getValue(),
        format: { dateStyle: 'short' },
      }),
  }),
  columnHelper.accessor('updatedAt', {
    header: 'Updated at',
    cell: ({ getValue }) =>
      dateFormatter({ date: getValue(), format: { dateStyle: 'short' } }),
  }),
  columnHelper.accessor('notes', {
    header: 'Notes',
    cell: ({ getValue, row }) => (
      <NotesPopover
        candidateId={row.id}
        notes={getValue()}
        iconClassName="ml-3"
      />
    ),
  }),
  ...(['admin.com', 'mindpal.co'].includes(profileEmailDomain ?? '')
    ? [
        columnHelper.accessor('messages', {
          header: 'Messages',
          cell: ({ getValue, row }) => (
            <MessagesDialog messages={getValue()} candidateId={row.id} />
          ),
        }),
      ]
    : []),
  columnHelper.accessor('convertedSalary', {
    meta: { altHeader: 'Salary' },
    id: 'converted_salary',
    enableSorting: true,
    header: ({ table }) => salaryHeader({ table }),
    cell: ({ getValue, table, row }) => (
      <Salary row={row} salary={getValue()} table={table} />
    ),
  }),
  columnHelper.display({
    id: 'settings',
    header: ({ table }) => <SettingsHeader table={table} />,
    cell: ({ row }) => <Settings row={row} />,
  }),
];
